import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { DropdownWithAccordion } from "../components/ui/dropdown";
import { SelectedFilterList } from "../components/features/SelectedFilterList";
import { useNavigate, useParams } from "react-router-dom";
import {
  accountsApi,
  useGetAccountsFiltersQuery,
  useGetAccountsListQuery,
  useGetCampaignSummaryQuery,
} from "../api";
import { Order } from "../types";
import { useEffect } from "react";
import { ResetAllFiltersButton } from "../components/features/ResetAllFiltersButton";
import { prepareFilters } from "../utils/prepareFilters";
import { InputWithQuery } from "../components/ui/InputWithQuery";
import { toast } from "react-toastify";
import { CustomToast } from "../components/ui/CustomToast";
import { getErrorMessage } from "../utils/getErrorMessage";
import { AppRoutes } from "../types/enums";
import {
  useAppDispatch,
  useAppSelector,
  useStateWithSearchParams,
} from "../hooks";
import { setUpdate } from "../features/companySlice";
import { AccountsListTable } from "../components/accounts";
import { AnalyzeAllBtn } from "../components/ui/buttons";
import { NoAccountData } from "../components/accounts/NoAccountData";
import { QUERY_SEPARATOR } from "../constant";
import { ExportMenu, GenerateReportsMenu } from "../components/features";

type SearchParams = {
  sizes?: string;
  industries?: string;
  regions?: string;
  search?: string;
  sort?: string;
  page?: string;
  per_page?: string;
  selected?: string;
  buyer_journey?: string;
  data_source?: string;
};

const AccountsListPage = () => {
  const { campaign_id } = useParams();
  const updateData = useAppSelector((state) => state.company.update);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    {
      industries: undefined,
      regions: undefined,
      sizes: undefined,
      search: undefined,
      sort: undefined,
      page: undefined,
      per_page: undefined,
      selected: undefined,
      buyer_journey: undefined,
      data_source: undefined,
    },
  );

  const filters = useGetAccountsFiltersQuery(
    {
      campaign_id: campaign_id!,
    },
    { skip: !campaign_id, refetchOnMountOrArgChange: true },
  );

  const { data, refetch, isFetching, error } = useGetAccountsListQuery(
    {
      campaign_id: campaign_id!,
      size: searchDict?.sizes?.split(QUERY_SEPARATOR),
      search: searchDict.search || undefined,
      industry: searchDict?.industries?.split(QUERY_SEPARATOR),
      region: searchDict?.regions?.split(QUERY_SEPARATOR),
      page: searchDict?.page ? Number(searchDict?.page) : undefined,
      per_page: searchDict?.per_page ? Number(searchDict?.per_page) : undefined,
      sort: searchDict?.sort?.split("-")[0],
      order: searchDict?.sort?.split("-")[1] as Order,
      buyer_journey: searchDict?.buyer_journey?.split(QUERY_SEPARATOR),
      data_source: searchDict?.data_source?.split(QUERY_SEPARATOR),
    },
    { skip: !campaign_id, refetchOnMountOrArgChange: true },
  );

  const { data: summary } = useGetCampaignSummaryQuery({
    campaign_id: campaign_id!,
  });

  const isSixSens = !!summary?.has_six_sense_sub;

  useEffect(() => {
    if (
      searchDict.buyer_journey ||
      searchDict.search ||
      searchDict.per_page ||
      searchDict.sort ||
      searchDict.sizes ||
      searchDict.industries ||
      searchDict.data_source ||
      searchDict.regions
    )
      setSearchValue("page", undefined);
  }, [
    searchDict.search,
    searchDict.per_page,
    searchDict.sort,
    searchDict.sizes,
    searchDict.industries,
    searchDict.buyer_journey,
    searchDict.regions,
    searchDict.data_source,
  ]);

  useEffect(() => {
    if (updateData === "list" && data) {
      refetch();
      dispatch(setUpdate(""));
      dispatch(accountsApi.util.invalidateTags(["analytics"]));
    }
  }, [updateData]);

  useEffect(() => {
    if (error) {
      toast.error(
        <CustomToast
          type={"error"}
          text={getErrorMessage(error).globalError || ""}
          title={"Something went wrong"}
        />,
      );
      navigate(AppRoutes.CAMPAIGNS);
    }
  }, [error]);

  const removeFilterHandler = (query: string, filter: string) => {
    let key = "journey stage" === query ? "buyer_journey" : query;
    key = "data source" === key ? "data_source" : key;

    const filters = searchDict[key as keyof SearchParams];
    if (!filters) return;

    setSearchValue(
      key as keyof SearchParams,
      filters
        .split(QUERY_SEPARATOR)
        .filter((el) => el !== filter)
        .join(QUERY_SEPARATOR),
    );
  };

  const isFilters = prepareFilters(filters.data).some(
    ({ query }) => searchDict[query as keyof SearchParams],
  );

  return (
    <Stack gap="16px">
      <Box sx={{ display: "flex", alignItems: "center", gap: "24px" }}>
        <InputWithQuery />
        {prepareFilters(filters.data).length > 0 && (
          <DropdownWithAccordion
            isActive={prepareFilters(filters.data).some(
              ({ query }) => searchDict[query as keyof SearchParams],
            )}
            data={prepareFilters(filters.data)}
            selected={prepareFilters(filters.data).reduce(
              (acc, { query }) =>
                acc +
                (searchDict[query as keyof SearchParams]?.split(QUERY_SEPARATOR)
                  ?.length || 0),
              0,
            )}
          />
        )}
        {isFilters && <ResetAllFiltersButton />}

        {summary && (
          <Stack
            direction="row"
            gap="12px"
            marginLeft="auto"
            alignItems="center"
          >
            <ExportMenu />

            <GenerateReportsMenu />

            {summary.analyze_all_status === "allowed" && (
              <AnalyzeAllBtn campaign_id={campaign_id!} />
            )}
          </Stack>
        )}
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        gap="16px"
        alignItems="flex-end"
      >
        <Stack direction="row" flexWrap="wrap" gap="8px">
          {prepareFilters(filters.data).map(({ placeholder, query }) => (
            <SelectedFilterList
              key={query}
              filters={
                searchDict[query as keyof SearchParams]?.split(
                  QUERY_SEPARATOR,
                ) || []
              }
              label={
                placeholder === "Buyer's Journey"
                  ? "Journey Stage"
                  : placeholder
              }
              removeHandler={removeFilterHandler}
            />
          ))}
        </Stack>
        {searchDict.selected && (
          <Typography
            component="span"
            variant="button"
            color="custom.darkGrey"
            noWrap={true}
            width="90px"
          >
            Selected: {searchDict.selected.split(QUERY_SEPARATOR).length}
          </Typography>
        )}
      </Stack>
      {isFetching && !data ? (
        <Box
          display="flex"
          height="25vh"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={80} sx={{ color: "#FE8836" }} />
        </Box>
      ) : (
        <>
          {data?.items?.length === 0 &&
          (isFilters ||
            searchDict.search ||
            !!searchDict.selected ||
            isFetching) ? (
            <NoAccountData />
          ) : (
            <AccountsListTable
              isSixSens={isSixSens}
              campaign_id={campaign_id!}
              rows={data?.items || []}
              totalRows={data?.count}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default AccountsListPage;
